/** Layout only. Every panel owns its own query, cadence and failure state, so
 *  one dead endpoint degrades its tile and leaves the board readable.
 *
 *  Two pipelines feed this board and they are now separate tabs — see
 *  components/PipelineTabs for why each rail entry carries its own figure and
 *  health dot rather than being a bare label. */
import { useEffect, useState } from 'react'
import { Section } from './components/ui'
import { LoadProvider, useLoad } from './api/load'
import { RangeProvider } from './api/range'
import { RangeChips } from './components/RangeChips'
import { LoadBar } from './components/LoadBar'
import { Login } from './auth/Login'
import { checkSession, logout } from './auth/session'
import { PipelineTabs, Panel } from './components/PipelineTabs'
import type { PipelineId, RailEntry, RailView } from './components/PipelineTabs'
import { StatusBand } from './panels/StatusBand'
import { Strips } from './panels/Strips'
import { Triage } from './panels/Triage'
import { Queues } from './panels/Queues'
import { PipelineHealth } from './panels/PipelineHealth'
import { Throughput, ClassifierThroughput } from './panels/Throughput'
import { Vidiq, VidiqThroughput, VidiqTiers, vidiqLane } from './panels/Vidiq'
import {
  EmbeddingsBreakdowns, EmbeddingsCoverage, EmbeddingsCost,
  EmbeddingsDeadLetter, EmbeddingsThroughput,
} from './panels/Embeddings'
import { Budget } from './panels/Budget'
import { Attribution } from './panels/Attribution'
import {
  ChannelBreakdowns, ChannelHeadline, VideoBreakdowns, VideoHeadline,
} from './panels/Classification'
import { ClassifierFlowBoard, FlowBoard } from './panels/FlowBoard'
import { EsActivity } from './panels/EsActivity'
import { TierActivity } from './panels/TierActivity'
import { Coverage } from './panels/Coverage'
import { Divergence } from './panels/Divergence'
import { Jobs } from './panels/Jobs'
import {
  QdrantClients, QdrantClusterPanel, QdrantLatency, QdrantLive,
  QdrantStatusMix, QdrantThroughput, QdrantTopQueries, useQdrantRail,
} from './panels/Qdrant'
import { QdrantKeys } from './panels/QdrantKeys'
import {
  TtdAssetPipeline, TtdCtvDaily, TtdCtvMatchedbyBuckets, TtdCtvStats, TtdSourceData,
} from './panels/Ttd'
import {
  ScoringApiJobs, ScoringFailures, ScoringFlow, ScoringHealth, ScoringOutcomes, ScoringProgress,
  useScoringRail,
} from './panels/Scoring'
import {
  useEsHealth,
  useClassifiedCounts,
  useClassifierDistributions,
  useVidiqCounts,
  useEmbeddings,
  useTtdAssetPipeline,
} from './api/queries'
import './App.css'
import './components/PipelineTabs.css'

/* The flow board is NOT optional and has no flag.
 *
 * It existed behind VITE_FLOWBOARD so it could be compared against the old
 * layout on the same data. That comparison is settled — the pipeline view is
 * the reason this board can answer "where is it stuck" at all, and a stray env
 * var must not be able to delete it from either tab. Both tabs render one;
 * src/__tests__/contracts.test.tsx pins that.
 */

/** Clicking a stage scrolls to the panel that details it. The flow board says
 *  WHERE the problem is; the panels say what it is. */
function scrollToStage(id: string) {
  const target =
    id === 'dead_letter' || id.includes('.') ? 'sec-pipeline'
      : id.endsWith('-worker') ? 'sec-pipeline'
        : id === 'postgres' || id === 'elasticsearch' ? 'sec-es'
          : 'sec-pipeline'
  jumpTo(target)
}

/** The rail's "On this page" group and the flow board share one jump. */
function jumpTo(id: string) {
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  document.getElementById(id)
    ?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' })
}

/* The sections of each pipeline, as the rail's jump group. Anchors, not
 * view-tabs — see the prop comment in PipelineTabs.tsx for why filtering the
 * page here would reintroduce the failure the rail exists to prevent. */
const VIEWS: Record<PipelineId, RailView[]> = {
  collector: [
    { id: 'sec-flow', label: 'Pipeline flow' },
    { id: 'sec-es', label: 'Elasticsearch' },
    { id: 'sec-cov', label: 'Coverage' },
    { id: 'sec-pipeline', label: 'Streams & throughput' },
    { id: 'sec-div', label: 'Consistency' },
    { id: 'sec-jobs', label: 'Upload jobs' },
  ],
  classifier: [
    { id: 'sec-cls-flow', label: 'Pipeline flow' },
    { id: 'sec-cls-tp', label: 'Throughput' },
    { id: 'sec-cls', label: 'Classified' },
    { id: 'sec-breakdowns', label: 'Breakdowns' },
  ],
  vidiq: [
    { id: 'sec-vidiq', label: 'vidIQ' },
  ],
  embeddings: [
    { id: 'sec-embeddings', label: 'Embeddings' },
  ],
  qdrant: [
    { id: 'sec-qd-live', label: 'Gateway now' },
    { id: 'sec-qd-tp', label: 'Throughput & latency' },
    { id: 'sec-qd-clients', label: 'Clients' },
    { id: 'sec-qd-keys', label: 'Keys' },
    { id: 'sec-qd-top', label: 'Top queries' },
    { id: 'sec-qd-cluster', label: 'Cluster' },
  ],
  ttd: [
    { id: 'sec-ttd-asset', label: 'ttd_asset pipeline' },
    { id: 'sec-ttd-source', label: 'Source ingestion' },
    { id: 'sec-ttd-daily', label: 'CTV daily traffic' },
    { id: 'sec-ttd-matchedby', label: 'Matched-by' },
    { id: 'sec-ttd-ctv', label: 'CTV stats' },
  ],
  scoring: [
    { id: 'sec-sc-flow', label: 'Pipeline flow' },
    { id: 'sec-sc-health', label: 'Health & throughput' },
    { id: 'sec-sc-rollout', label: 'Re-score progress' },
    { id: 'sec-sc-outcomes', label: 'Outcomes' },
    { id: 'sec-sc-failures', label: 'Failures' },
    { id: 'sec-sc-api', label: 'API & jobs' },
  ],
}

export default function App() {
  const [authed, setAuthed] = useState<boolean | null>(null)

  // Gate the board on the session cookie the collector's /auth endpoints
  // manage (Task 6). checkSession() runs once on mount and fails OPEN on
  // anything short of a definite 401/403 (see its own comment) -- a
  // restarting collector must degrade like every other endpoint on this
  // board, not evict an authenticated operator to the login form. The
  // `dash:unauthorized` listener (client.ts dispatches it on 401 ONLY) is
  // what actually catches a session that dies MID-SESSION, rather than
  // leaving every panel to render its own empty/error state silently --
  // which would look like a data outage, not an expired login.
  useEffect(() => {
    checkSession().then(setAuthed)
    const onUnauthorized = () => setAuthed(false)
    window.addEventListener('dash:unauthorized', onUnauthorized)
    return () => window.removeEventListener('dash:unauthorized', onUnauthorized)
  }, [])

  if (authed === null) return null
  if (!authed) return <Login onSuccess={() => setAuthed(true)} />

  return (
    <LoadProvider>
      <RangeProvider>
        <button className="logout" title="Sign out"
                onClick={() => logout().catch(() => {}).finally(() => setAuthed(false))}>
          Sign out
        </button>
        <Board />
      </RangeProvider>
    </LoadProvider>
  )
}

/** The rail's own data.
 *
 *  Deliberately reuses figures the panels already fetch rather than adding a
 *  query of its own: these hooks are shared through the query cache, so the
 *  rail costs nothing extra on top of the panels it summarises. */
function useRail(): RailEntry[] {
  const es = useEsHealth()
  const cls = useClassifiedCounts()
  const dist = useClassifierDistributions()
  const vidiq = useVidiqCounts()
  const embeddings = useEmbeddings()
  const qdrant = useQdrantRail()
  const ttd = useTtdAssetPipeline()
  // Scoring's entry is built where its judgement lives (panels/Scoring.tsx,
  // `scoringRail`): figure = channels scored TODAY, dot = the worst crit/warn
  // across the findings of all four finding-bearing endpoints — the same
  // server-side findings Triage and the alert email render.
  const scoring = useScoringRail()

  // /metrics/es/health reports PER INDEX, so the rail folds them: documents are
  // summed, and the worst index sets the dot. A stale sink is the failure that
  // hides — every other number still renders while the data behind it ages,
  // which is how 11 days went unnoticed on this estate — so one stale index
  // must not be averaged away by several healthy ones.
  const esIndices = es.data && 'indices' in es.data
    ? Object.values(es.data.indices) : []
  const esDocs = esIndices.reduce(
    (a, i) => a + ('docs' in i ? i.docs : 0), 0)
  const esErrored = esIndices.some(i => !('docs' in i))
  const esStale = esIndices.some(i => 'stale' in i && i.stale)
  const esConfigured = es.data?.configured === true

  const collectorState: RailEntry['state'] =
    es.isError || esErrored ? 'crit'
      : esStale ? 'warn'
      : esConfigured ? 'ok' : 'idle'

  const classifierState: RailEntry['state'] =
    cls.isError ? 'crit'
      : dist.data?.tier_1_ready === false ? 'warn'
      : cls.data ? 'ok' : 'idle'

  // STALENESS IS THE DOT, not row count or container health. Both of those read
  // normal throughout the predecessor's three-month silent outage; "how long
  // since a row landed" is the only signal that would have caught it on day
  // one. 2h is several multiples of a healthy cycle's gap between writes.
  // Unless the lane has simply caught up: see vidiqLane.
  const vidiqNow = vidiqLane(vidiq.data)
  const vidiqState: RailEntry['state'] =
    vidiq.isError ? 'crit'
      : vidiqNow === 'stalled' ? 'crit'
      : vidiqNow === 'idle' ? 'idle'
      : vidiq.data ? 'ok' : 'idle'

  // Documents actually embedded, video + channel. `embedded` (not `done`
  // alone -- SIL-10670 coverage-counts-vectors fix), falling back to `done`
  // against an older collector that has not deployed `embedded` yet: `done`
  // alone undercounts `channel_classification`, which carries ~3.29M
  // documents a predecessor GPU pipeline vectorised before this pipeline's
  // `embed_state` bookkeeping existed -- they are genuinely embedded but
  // never got the `done` marker written, and summing `done` alone here would
  // repeat the exact "13.1% done" undercount on the rail ticker that the
  // Embeddings tab itself was fixed for. Not a health signal on its own --
  // a large total sitting beside a pipeline that has quietly stopped would
  // repeat vidIQ's own lesson (a big cumulative count reads as reassurance no
  // matter how stale it is) -- so the DOT is driven by `failed` instead: a
  // mapper that is merely behind leaves documents `pending`, which is
  // ordinary, but one that is actively erroring marks them `failed`, which
  // is not.
  const embDone = embeddings.data
    ? (embeddings.data.coverage?.video?.embedded ?? embeddings.data.coverage?.video?.done ?? 0)
      + (embeddings.data.coverage?.channel?.embedded ?? embeddings.data.coverage?.channel?.done ?? 0)
    : undefined
  const embFailed = embeddings.data
    ? (embeddings.data.coverage?.video?.failed ?? 0) + (embeddings.data.coverage?.channel?.failed ?? 0)
    : 0
  const embeddingsState: RailEntry['state'] =
    embeddings.isError ? 'crit'
      : embFailed > 0 ? 'warn'
      : embeddings.data ? 'ok' : 'idle'

  // Total ttd_asset rows reaching any pipeline stage, trailing 7 days — the
  // same figure TtdAssetPipeline leads with. Cumulative, not a health signal
  // on its own (the same caveat vidiq's rail figure carries): a large total
  // sitting beside a pipeline that has quietly stopped would read as
  // reassurance regardless of staleness, so the DOT is driven by whether the
  // read succeeded AND actually reported rows — `ttd.data ? 'ok' : 'idle'`
  // would read a genuinely empty `{ counts: [] }` response as 'ok' (an
  // object is truthy even when its array is empty), the same
  // zero-reads-as-healthy failure this board's null/empty discipline exists
  // to forbid elsewhere (Queues.rate_per_s, VidiqTiers.captured_at).
  const ttdTotal = ttd.data?.counts.reduce((a, c) => a + c.count, 0)
  const ttdState: RailEntry['state'] =
    ttd.isError ? 'crit' : (ttd.data?.counts.length ?? 0) > 0 ? 'ok' : 'idle'

  return [
    {
      id: 'collector',
      name: 'Collector',
      repo: 'mirrors-collector',
      value: es.isError ? null : esConfigured ? esDocs : undefined,
      unit: 'docs mirrored',
      state: collectorState,
      note: es.isError
        ? 'Elasticsearch health could not be read'
        : esErrored
          ? 'one or more indices did not report'
          : esStale
            ? 'an index has not been written recently'
            : undefined,
    },
    {
      id: 'classifier',
      name: 'Classifier',
      repo: 'mirrors-classifier',
      value: cls.isError ? null : cls.data?.total,
      unit: 'channels classified',
      state: classifierState,
      note: cls.isError
        ? 'classifier metrics API could not be read'
        : dist.data?.tier_1_ready === false
          ? 'tier_1 breakdown unavailable — migration 002 has not run'
          : undefined,
    },
    {
      id: 'vidiq',
      name: 'vidIQ',
      repo: 'mirrors-collector · vidiq',
      // This figure is CUMULATIVE -- see the VidiqCounts schema, which is the
      // contract /metrics/vidiq actually serves. It is deliberately NOT the
      // signal: a lifetime count read ~147M on every day of the three months
      // the previous pipeline spent writing nothing, so it cannot distinguish
      // a working pipeline from a dead one and must never be read as if it
      // could. The DOT below carries that judgement, and it is driven purely
      // by staleness. The count is here for scale, not for health.
      value: vidiq.isError ? null : vidiq.data?.rows_written,
      unit: 'rows written',
      state: vidiqState,
      note: vidiq.isError
        ? 'vidiq metrics could not be read'
        : vidiqNow === 'stalled'
          ? `no row written for ${Math.round((vidiq.data?.stale_s ?? 0) / 3600)}h`
          : vidiqNow === 'idle' ? 'caught up, nothing queued' : undefined,
    },
    {
      id: 'embeddings',
      name: 'Embeddings',
      repo: 'mirrors-collector · embeddings',
      value: embeddings.isError ? null : embDone,
      unit: 'docs embedded',
      state: embeddingsState,
      note: embeddings.isError
        ? 'embeddings metrics could not be read'
        : embFailed > 0
          ? `${embFailed} document${embFailed === 1 ? '' : 's'} failed embedding`
          : undefined,
    },
    {
      id: 'qdrant',
      name: 'Qdrant',
      repo: 'silverpush/qdrant-deploy',
      // Live search QPS, not a cumulative count: this pipeline's failure mode
      // is refusing or slowing queries NOW, so the rail carries the only
      // figure that changes when that happens. The dot is the server's own
      // worst-wins verdict (sampler silence, dead replica, breaker, raft) —
      // computed server-side so the rail and the tab can never disagree.
      value: qdrant.value,
      unit: 'qps · search',
      state: qdrant.state,
      note: qdrant.note,
    },
    {
      id: 'ttd',
      name: 'TTD',
      repo: 'mirrors-collector · ttd',
      value: ttd.isError ? null : ttdTotal,
      unit: 'assets · 7d',
      state: ttdState,
      note: ttd.isError ? '/metrics/ttd could not be read' : undefined,
    },
    scoring,
  ]
}

function Board() {
  const { loaded } = useLoad()
  const [active, setActive] = useState<PipelineId>('collector')
  const rail = useRail()
  // The one piece of cross-panel state on the qdrant tab: a click in the
  // clients table scopes the top-queries panel below it to that caller.
  // A SINGLE source of truth, kept honest in both directions: a click sets
  // it, but top-queries' own `onClientChange` back-channel ALSO sets it —
  // to whatever `p.client` actually is down there, including '' the moment
  // its own select clears the filter. That is what keeps QdrantClients'
  // `selected` marker from going stale, and it is also what makes picking
  // the SAME client again after clearing it a real change rather than a
  // no-op: by the time of the re-pick, this state was already corrected to
  // '' by the back-channel, so 'classifier' after '' is a genuine
  // transition even though the string repeats.
  const [qdrantClient, setQdrantClient] = useState<string>('')

  return (
    <>
      <header className="top">
        <div className="top-in">
          <span className="logo">mirrors <span>/ mission control</span></span>
          <RangeChips />
        </div>
      </header>

      <main className="wrap">
        <div className="loadrow"><LoadBar /></div>
        {/* The verdict leads the page, above the rail and outside the tabs.
          *
          * It spans all three pipelines deliberately: a classifier stall has to
          * reach someone who is reading the collector tab, which is the same
          * reason the rail carries a figure for the pipeline you are not on.
          * Scoping this inside <Panel> would have reintroduced exactly the blind
          * spot the rail was built to close. */}
        {loaded && <Triage />}
        {/* The strips report all three pipelines at once and therefore live
          * outside the tabs, beside the verdict — the same reason Triage does.
          * A per-tab copy would hide two thirds of the board behind a tab
          * switch, which is the 11-day outage's exact mechanism. */}
        {loaded && <Strips />}
      </main>

      {/* The rail renders ALWAYS, not only once data is loaded.
        *
        * It was inside the `loaded` branch, so a fresh page showed the empty
        * state and no tabs at all — the board looked like it had never gained
        * the feature. Navigation is not a result: hiding it until a query
        * returns means a reader cannot even see which pipelines exist, let
        * alone choose one, and a failed load would hide the switcher entirely.
        *
        * The rail's own figures degrade on their own (undefined renders "—"),
        * which is exactly what they are for. */}
      <div className="shell">
        <PipelineTabs entries={rail} active={active} onSelect={setActive}
                      views={loaded ? VIEWS[active] : undefined} onJump={jumpTo} />

        {!loaded ? (
          <p className="empty">
            Press <b>Load data</b> to pull the current pipeline state.
          </p>
        ) : (
          <div>
            <Panel id="collector" active={active}>
              {/* WALL: the at-a-glance tiles lead every pipeline. Big figures
                  readable from metres away; any tile clicks through to a modal
                  with its exact numbers — details on demand, not by scroll.
                  Derived from the same hooks as the panels below, so the wall
                  cannot disagree with them. */}
              <StatusBand />

              {/* The pipeline, drawn as the pipeline. Above the panels rather
                  than replacing them: the board answers "where is it stuck"
                  positionally, and the panels below remain the detail you drill
                  into once you know where to look. Clicking a stage scrolls to
                  its panel.

                  No flag: this view is why the board can answer "where is it
                  stuck", and a build-time switch that deletes it is a
                  liability. */}
              <Section id="sec-flow" title="Pipeline flow"
                       hint="topology served by the API · rates between loads" />
              <div className="grid"><FlowBoard onSelect={scrollToStage} /></div>

              <Section id="sec-es" title="Elasticsearch activity"
                       hint="inserted = same-day iat/uat · updated = days differ · channel updates include scoring writes" />
              <div className="grid g2"><EsActivity /></div>
              {/* The same aggregate inserted/updated question EsActivity asks,
                  split by subscriber tier. Full width, directly beneath: this
                  is the expensive companion read (442M video / 28M channel
                  rows), so — unlike EsActivity above it — it is manual-refresh
                  only and shows a cached snapshot by default. See
                  TierActivity.tsx's own module doc for why it does not poll. */}
              <div className="grid"><TierActivity /></div>

              <Section id="sec-cov" title="Inventory coverage" hint="addressable → covered → pending" />
              <div className="grid g2"><Coverage /></div>

              <Section id="sec-pipeline" title="Pipeline"
                       hint="Redis Streams · shared quota ledger" />
              <div className="grid g3">
                <Queues />
                {/* Beside the depths deliberately: "the queue is deep" and
                    "the queue is deep AND nothing is draining it" render
                    identically without this, and only the second is an
                    incident. */}
                <Throughput />
                <Budget />
              </div>
              {/* Full width, directly under the same row: this is the signal
                  none of the three tiles above it can give. Queues/Throughput/
                  Budget all read Redis directly, and Redis read healthy
                  throughout the incident this panel exists to catch — the
                  degradation was one layer down, in the database underneath
                  it, and nothing here asked that question until now. Two
                  tables plus its own live caption need more room than a g3
                  column gives, hence its own full-width row rather than a
                  fourth slot beside the others. */}
              <div className="grid">
                <PipelineHealth />
              </div>
              <div className="grid g2">
                <Attribution />
              </div>

              <Section id="sec-div" title="Consistency" hint="system of record vs replica" />
              <div className="grid g2"><Divergence /></div>

              <Section id="sec-jobs" title="Upload jobs" />
              <div className="grid"><Jobs /></div>
            </Panel>

            <Panel id="classifier" active={active}>
              {/* Two lanes, because channel and video are two pipelines that
                  share nothing but a sink type. DeepSeek and the vLLM fleet are
                  drawn as stages: the eleven-hour classify stall was a DeepSeek
                  daily cap, and this tab had nowhere to show it. */}
              <Section id="sec-cls-flow" title="Pipeline flow"
                       hint="two lanes · upstreams shown as stages" />
              <div className="grid"><ClassifierFlowBoard /></div>

              {/* The classify worker held its heartbeat and completed nothing
                  for eleven hours on 2026-08-01. Liveness alone could not tell
                  that from a drained queue; a rate can. */}
              <Section id="sec-cls-tp" title="Throughput" hint="completed work per second" />
              <div className="grid g2"><ClassifierThroughput /></div>

              {/* Headlines side by side, breakdowns across the full width.
                  Pairing each headline with its own breakdown tile is what
                  stranded a four-stat card beside a fifty-row list and left
                  ~900 px of dead space on this tab. */}
              <Section id="sec-cls" title="Classified"
                       hint="channels and videos · 1d / 7d / 30d" />
              <div className="grid g2"><ChannelHeadline /><VideoHeadline /></div>

              <Section id="sec-breakdowns" title="Breakdowns"
                       hint="top 8 each · tail summarised" />
              <div className="grid g2"><ChannelBreakdowns /><VideoBreakdowns /></div>
            </Panel>

            <Panel id="vidiq" active={active}>
              {/* STALENESS FIRST, and it is the whole reason this tab exists.
                  The pipeline this replaced wrote its last row on 2026-04-21
                  and went on reporting healthy for three months: containers up,
                  queue drained, no errors. Row counts and liveness both read
                  normal throughout. "How long since a row landed" is the only
                  figure that would have caught it, so it leads — the wall's
                  freshness tile is the first thing on the tab. */}
              <Section id="sec-vidiq" title="vidIQ" hint="channel statistics · daily series" />
              <div className="grid g2"><Vidiq /><VidiqThroughput /></div>
              {/* Below the freshness pair, and full width. The two tiles above
                  answer "is it writing at all"; this answers "and is every
                  tier's data actually current" -- which a single whole-table
                  figure cannot, because the most valuable tiers are the fewest
                  rows and vanish into any average. */}
              <VidiqTiers />
            </Panel>

            <Panel id="qdrant" active={active}>
              {/* The live strip leads: this is the board's one genuinely live
                  tab (60 s poll — queries.ts documents the exception), and
                  "is the gateway admitting work right now" is the question it
                  exists to answer before any history. */}
              <Section id="sec-qd-live" title="Gateway now"
                       hint="QdrantMapper · polls every 60s · reads the metrics store only, never the gateway" />
              <div className="grid"><QdrantLive /></div>

              <Section id="sec-qd-tp" title="Throughput & latency"
                       hint="per lane · queue wait is backpressure, shown apart from gateway overhead" />
              <div className="grid"><QdrantThroughput /></div>
              <div className="grid g2"><QdrantLatency /><QdrantStatusMix /></div>

              <Section id="sec-qd-clients" title="Clients"
                       hint="per mapper client key · overall pinned first · click a caller to filter top queries" />
              <div className="grid">
                <QdrantClients selected={qdrantClient} onPick={(c) => {
                  setQdrantClient(c)
                  jumpTo('sec-qd-top')
                }} />
              </div>

              <Section id="sec-qd-keys" title="Keys"
                       hint="mapper client keys · create, rotate, revoke — session-gated, never the gateway itself" />
              <div className="grid"><QdrantKeys /></div>

              <Section id="sec-qd-top" title="Top queries"
                       hint="completed/failed/shed signatures — in-flight work is the strip above, by design" />
              <div className="grid">
                <QdrantTopQueries client={qdrantClient} onClientChange={setQdrantClient} />
              </div>

              <Section id="sec-qd-cluster" title="Cluster"
                       hint="sgp1 · three data nodes, both collections, raft" />
              <div className="grid"><QdrantClusterPanel /></div>
            </Panel>

            <Panel id="embeddings" active={active}>
              {/* Coverage leads, the same reason it leads the collector tab:
                  "how many processed or not" is the question this tab exists
                  to answer first. Cost and the daily trend follow it, then the
                  breakdowns -- volume alone would hide partial coverage inside
                  a busy-looking country or language. */}
              <Section id="sec-embeddings" title="Embeddings"
                       hint="DeepInfraMapper · coverage, cost and cache effectiveness" />
              <div className="grid g2"><EmbeddingsCoverage /></div>
              {/* Full width and directly under coverage: a dead-lettered
                  document is paid-for work that is permanently gone, which
                  makes it the most consequential figure on this tab -- it
                  does not share a row with anything else. */}
              <div className="grid"><EmbeddingsDeadLetter /></div>
              {/* Cost and throughput are windowed over the SAME rows and the
                  same four bands, so they sit side by side rather than one
                  above the other -- an operator reading "how much" next to
                  "how fast" is the point of pairing them. */}
              <div className="grid g2"><EmbeddingsCost /><EmbeddingsThroughput /></div>
              <div className="grid g2"><EmbeddingsBreakdowns /></div>
            </Panel>

            <Panel id="ttd" active={active}>
              {/* Pipeline stage counts lead, the same reason Coverage leads
                  the collector tab: "how much has moved through each stage"
                  is the first question this content pipeline answers. */}
              <Section id="sec-ttd-asset" title="ttd_asset pipeline"
                       hint="Postgres · matching / identification / extraction / subtitling / categorization" />
              <div className="grid"><TtdAssetPipeline /></div>

              <Section id="sec-ttd-source" title="Source ingestion & classification"
                       hint="ttd_source_data · Postgres · per source" />
              <div className="grid"><TtdSourceData /></div>

              <Section id="sec-ttd-daily" title="CTV daily traffic"
                       hint="ctv_stats · ClickHouse · matched vs not matched, per day" />
              <div className="grid"><TtdCtvDaily /></div>

              <Section id="sec-ttd-matchedby" title="Matched-by"
                       hint="ctv_stats · ClickHouse · genre / content id / series / title series / channel genre, count and share of total" />
              <div className="grid"><TtdCtvMatchedbyBuckets /></div>

              <Section id="sec-ttd-ctv" title="CTV stats"
                       hint="ctv_stats · ClickHouse · summary, matched-by breakdown, top failing urls" />
              <div className="grid g3"><TtdCtvStats /></div>
            </Panel>

            <Panel id="scoring" active={active}>
              {/* WHERE IT IS STUCK, first. The flow is drawn as the service is
                  built (feeder → Mongo queue → calculators ← ClickHouse →
                  supplier → channel_info) and each stage is lit only by a
                  server finding that names it — so this tab, Triage, the rail
                  and the alert email can never disagree. */}
              <Section id="sec-sc-flow" title="Pipeline flow"
                       hint="feeder → queue → calculators → supplier → channel_info · lit by the collector's findings" />
              <div className="grid"><ScoringFlow /></div>

              {/* FRESHNESS LEADS: workers beating while nothing is scored or
                  written is the failure that would hide here, and "since
                  scored / written" is the figure that catches it. Every
                  freshness figure states the age of its own reading — the
                  board holds a load for 24 h. */}
              <Section id="sec-sc-health" title="Health & throughput"
                       hint="freshness leads · rates since the server's last sample · /metrics/scoring/overview" />
              <div className="grid g2"><ScoringHealth /></div>

              <Section id="sec-sc-rollout" title="Re-score progress"
                       hint="v2 re-score of channel_info · counted while small, else a planner estimate" />
              <div className="grid"><ScoringProgress /></div>

              {/* The board's 1/2/7/30-day range drives the request; the tiles
                  label their window from the PAYLOAD (window_days), which is
                  what is actually on screen while a new range loads. */}
              <Section id="sec-sc-outcomes" title="Outcomes"
                       hint="bands, gates, tiers and data age · shares the board range" />
              <div className="grid g2"><ScoringOutcomes /></div>

              <Section id="sec-sc-failures" title="Failures"
                       hint="scoring:failures ring · newest 100 · repeated channels flagged" />
              <div className="grid g2"><ScoringFailures /></div>

              <Section id="sec-sc-api" title="API & jobs"
                       hint="scoring API health · Celery backlog · channel_score_jobs" />
              <div className="grid g2"><ScoringApiJobs /></div>
            </Panel>

            <footer className="foot mono">
              Read-only, and pulled on demand — nothing polls. Every figure is a
              Redis read, a bounded indexed query, or a pre-computed snapshot;
              nothing here runs an unbounded scan. Results are held for 24 h on
              both sides, and every snapshot-backed panel states its own age, so
              a stale number is never presented as current. Switching pipelines
              changes which panels are shown, never whether a problem is
              reported — the rail keeps both pipelines' health visible.
            </footer>
          </div>
        )}
      </div>
    </>
  )
}
